import { useState, useEffect, useRef, useMemo, useCallback } from 'react';

// Local inline-SVG chevron (matches project inline-icon style).
const ChevronDown = ({ className = 'w-4 h-4' }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

export interface ProjectFilterOptions {
  projects: string[];
  hasNoProject: boolean;
}

export interface ProjectFilterValue {
  hidden: string[];
  hideNoProject: boolean;
}

export interface ProjectFilterDropdownProps {
  options: ProjectFilterOptions;
  value: ProjectFilterValue;
  onChange: (next: ProjectFilterValue) => void;
}

export function ProjectFilterDropdown({
  options,
  value,
  onChange,
}: ProjectFilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  const hiddenSet = useMemo(() => new Set(value.hidden), [value.hidden]);

  const effectiveHiddenCount = useMemo(
    () => options.projects.filter((p) => hiddenSet.has(p)).length,
    [options.projects, hiddenSet]
  );

  const hidingNoProject = options.hasNoProject && value.hideNoProject;
  const isFilterActive = effectiveHiddenCount > 0 || hidingNoProject;

  const projectButtonLabel = useMemo(() => {
    if (!isFilterActive) return 'All projects';
    const totalOptions = options.projects.length + (options.hasNoProject ? 1 : 0);
    const visible = totalOptions - effectiveHiddenCount - (hidingNoProject ? 1 : 0);
    return `Projects: ${visible} of ${totalOptions}`;
  }, [isFilterActive, options.projects, options.hasNoProject, effectiveHiddenCount, hidingNoProject]);

  const handleToggleProject = useCallback(
    (name: string) => {
      const nextHidden = new Set(value.hidden);
      if (nextHidden.has(name)) nextHidden.delete(name);
      else nextHidden.add(name);
      onChange({ ...value, hidden: [...nextHidden] });
    },
    [value, onChange]
  );

  const handleToggleNoProject = useCallback(() => {
    onChange({ ...value, hideNoProject: !value.hideNoProject });
  }, [value, onChange]);

  const handleShowAll = useCallback(() => {
    onChange({ hidden: [], hideNoProject: false });
  }, [onChange]);

  const handleHideAll = useCallback(() => {
    onChange({
      hidden: [...options.projects],
      hideNoProject: options.hasNoProject,
    });
  }, [options, onChange]);

  return (
    <div className="relative ml-2" ref={containerRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors border ${
          isFilterActive
            ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
        }`}
        title={projectButtonLabel}
      >
        <span className="truncate max-w-[150px]">{projectButtonLabel}</span>
        <ChevronDown
          className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
            <button
              onClick={handleShowAll}
              className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
            >
              All
            </button>
            <button
              onClick={handleHideAll}
              className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
            >
              None
            </button>
          </div>
          {options.projects.length === 0 && !options.hasNoProject ? (
            <div className="px-4 py-3 text-sm text-gray-400">No projects found</div>
          ) : (
            <div className="py-1">
              {options.projects.map((name) => {
                const checked = !hiddenSet.has(name);
                return (
                  <label
                    key={name}
                    className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      checked={checked}
                      onChange={() => handleToggleProject(name)}
                    />
                    <span className="truncate text-gray-700">{name}</span>
                  </label>
                );
              })}
              {options.hasNoProject && (
                <label className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50 cursor-pointer border-t border-gray-100">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    checked={!value.hideNoProject}
                    onChange={handleToggleNoProject}
                  />
                  <span className="truncate text-gray-500">No project</span>
                </label>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
